/** Every explanation the product keeps behind a small control - **ADR-0212 decision 2, the text diet**.
 *
 * A data screen is a title, tiles, one filter line and one table, with at most one plain sentence of
 * body text. Explanation, basis and limits, caveats, definitions and honesty notes do not live in the
 * screen's markup: they live here, keyed by screen and topic, and a screen shows them through an
 * `InfoPopover` (a few sentences beside a label) or a `HowDrawer` ("How this is calculated"). **Moving
 * text is the rule; deleting it is not allowed** - the receipt drawer, the filing's checks and Help
 * carry the regulatory and method wording and are its homes, so rule 3 and the honesty clauses hold
 * where a person looks for them.
 *
 * Why one file: Help can list every explanation, a reviewer can see in one diff what a package moved
 * out of a screen, and a screen cannot invent a paragraph in a component. `infoText.test.ts` fails
 * for a key an `InfoPopover` or `HowDrawer` names that is not defined here, and for a key defined here
 * that no component uses.
 *
 * **Who edits what.** The structure of this file (the sections, the entry shape, the key types) is
 * the shell package's. A package adds entries **under its own screen's section only**, as it moves
 * text out of that screen; a section is empty until then. A key is `section.topic`, and the topic is
 * a short kebab-case name for the thing explained ("how-calculated", "support-level"), never a
 * sentence.
 */

export interface InfoEntry {
  /** The control's accessible name and the drawer's heading. A few words. */
  title: string;
  /** Paragraphs, in order. Plain sentences: a limit stated as a limit, not softened. */
  body: readonly string[];
}

/** One section per place in the rail, plus `shell` (chrome and shared components) and `record` (the
 * record page, which is not a place). */
export const INFO_TEXT = {
  shell: {
    "not-built": {
      title: "Not available yet",
      body: [
        "This place has no screen yet. Nothing on it is sample data, and nothing here is a stand-in for a real figure.",
      ],
    },
    "path-to-filing": {
      title: "How the path is drawn",
      body: [
        "The path has five stations, in order: Data in, Figures verified, Approvals, Disclosures complete and Issued.",
        "Each station is done, current, blocked, ahead or not checked. The current station is the first one that is not done, and the blocker named beside it is the first check that stops it.",
        "The path shows where the filing stands. It is not an approval and it does not say a report is fit to file; that is a person's judgement.",
      ],
    },
    // ---- table power features (P14): one contiguous block, keep additions together ----
    "table-views": {
      title: "Saved views",
      body: [
        "A view remembers the filters, the sort, the visible columns and the grouping of this table. A dot on a tab means the table has changed since the view was saved.",
        "Views are kept for you in this browser only. They do not follow you to another browser or device, because an account-level store needs an endpoint that does not exist yet. The shipped views are locked: duplicate one to change it.",
      ],
    },
    "table-subtotals": {
      title: "Group subtotals",
      body: [
        "A subtotal adds up the rows of that group that are loaded in this table, in exact decimal arithmetic. It is not a figure with a receipt of its own; each row's figure still opens its receipt.",
        "A row with no figure is left out of the sum and the cell says how many. The total under the table is still EmitIQ's own figure, and it is shown only when no filter narrows the list.",
      ],
    },
    // ---- end of the table power block ----
  },
  home: {
    total: {
      title: "Total emissions",
      body: [
        "The inventory's own figure for the period in force, location-based unless the Advanced view is switched on in My settings. It is EmitIQ's total and is never added up on this page.",
        "The total and each scope under it open the figures behind them, and each figure opens its receipt.",
      ],
    },
    "next-due": {
      title: "Next filing due",
      body: [
        "The soonest date still to come among the filings that are law now and not yet issued, or issued and owing work again. A filing whose date has passed is not hidden: it is counted as overdue, in red, with the days it is late, and the next date still ahead is shown beside it.",
        "A regime that is enjoined or only proposed is never counted down to, and an issued filing with nothing owed is done. The dates are the regulation's, as loaded for this workspace.",
        "A filing with no due date, or a position not yet settled, has no date here. The path and the reason are on the filing.",
      ],
    },
  },
  actions: {
    waiting: {
      title: "What is waiting on you",
      body: [
        "Approvals that name you at their current stage, tasks assigned to you, and bills to review, which have nobody named and are the work of whoever may upload. The count here is the one in the bell and on Home.",
        "Vendor matches to confirm and estimates awaiting a second person are not listed here yet: they are not a kind of item the product raises today.",
        "A role that is never asked for anything shows a dash, not zero.",
      ],
    },
    "how-decisions": {
      title: "How a decision is made",
      body: [
        "A route is an ordered set of stages, each naming a reviewer. A request is listed when its current stage names you; a stage you are not named on is not yours to decide.",
        "You decide with the figure's receipt in front of you. There are three answers: approve; request an adjustment, which leaves the stage open and raises a task for whoever submitted it; and reject. Asking for a change is not a rejection, and the decision history is permanent.",
        "The person who submitted a request cannot decide it, and an open blocking flag stops an approval. EmitIQ refuses both, and the row says when a flag is in the way.",
      ],
    },
    submitted: {
      title: "Submitted by me",
      body: [
        "Everything you sent for approval, in any state, newest first. Counts are EmitIQ's; the table sorts the page it holds and says nothing about the rest.",
        "An Admin, an Approver or a Contributor sends a record for approval, so this list exists only for them. What is in review is read on the record it is about.",
      ],
    },
    decided: {
      title: "Decided",
      body: [
        "Requests approved or rejected in this workspace, with who decided and when. No rationale is shown here: it stays in the workspace and out of the ledger.",
        "An Admin or an Approver sees what has been decided. Your own requests are in Submitted by me, with their outcome.",
      ],
    },
    "tasks-how": {
      title: "How the queue works",
      body: [
        "The queue is ordered by how long a task has waited, as EmitIQ returns it, and is not re-sorted here: newest-first would hide the item most worth finding. It opens on your own work; choose Everyone's work to see everyone's.",
        "A task raised from a record and a task that stands alone appear together, because a contributor's day is one list. Closing a task is a statement that the work was done, so it needs a note saying what; the note is what lets someone else check it. New task adds one for yourself (an Admin can give it to someone else), and the same control on a record adds a task about that record.",
        "The counts above cover the tasks this list holds, not the whole workspace, and are not shown while the list cannot be read.",
        "An Admin can pass a task to someone else and say why; the reason is shown to the new owner on the task and is not copied into the activity log or the notification. The owner of an open task is reminded in the app when it is due the next day or is late, once for each due date.",
      ],
    },
    notifications: {
      title: "Notifications",
      body: [
        "In-app only. Every sentence is EmitIQ's, written when you read it from things you can see; this page composes none.",
        "Opening a notification marks it read. The button marks it read without leaving, and Mark all as read clears every unread one in a single step. Only the newest 50 are listed. Email is off.",
      ],
    },
  },
  ask: {
    off: {
      title: "AI is off",
      body: [
        "Nothing here reads your data with a model. No question you type would be sent anywhere, and no answer is made up in its place. AI-assisted features stay off until the data-processing terms, the customer notice and the transfer assessment they depend on are in place, and an administrator of this workspace turns them on.",
        "The questions listed open the screen that answers them from your own figures. Press the search box at the top (or Ctrl K / Cmd K) to jump to any screen or Help section by name.",
      ],
    },
  },
  scope1: {
    // The explanations of the three scope tables (Scope 1, Scope 2 and Scope 3 share the grade, status
    // and source texts below: they are one grid with one meaning).
    "how-calculated": {
      title: "How Scope 1 is calculated",
      body: [
        "Each figure is a quantity multiplied by a locked emission factor, worked out in exact kilograms. The total is divided by 1,000 and rounded only for display.",
        "A refrigerant is composed from the global warming potential of each gas it contains.",
        "Totals are summed by EmitIQ over the figures in force. A figure that a later revision replaced is not in them, and every figure opens its receipt.",
      ],
    },
    grades: {
      title: "Data quality grades",
      body: [
        "Grade A is a reading EmitIQ can trace to a bill it holds. Grade B is a reading a person recorded with no document, or a split from a larger figure. Grade C is an estimate: last year's figure used again, or a model from the weather.",
        "Estimates counts every figure below grade A. EmitIQ sets the grade from how the figure was reached; nobody types one.",
      ],
    },
    status: {
      title: "What the status means",
      body: [
        "Verified: a person accepted the figure and its evidence chain holds.",
        "Awaiting accept: entered by hand and not yet accepted. It already counts in the totals, and a different Admin or Approver accepts it.",
        "Not verified: no verifier is recorded for it. Evidence gap: it was accepted, but a link in its evidence chain breaks, and its receipt says where.",
      ],
    },
    source: {
      title: "Where a figure came from",
      body: [
        "Uploaded: read from a bill someone put in. Manual: typed in by a person, with the basis they gave. Imported: arrived through a spreadsheet, a supplier's answer or another file. Connector: pulled in by a connector.",
        "This is worked out when the page opens and is never stored, so it cannot disagree with the document or the person behind the figure.",
      ],
    },
    missing: {
      title: "Missing bills",
      body: [
        "Counts the expected bills that were due and have not arrived.",
        "A period that is not due yet is pending, not missing, and a confirmed none is not missing either. Processing, Missing lists every period and its state.",
      ],
    },
    // Shared by every screen that shows figures for a period (the empty state `NoPeriod`).
    "no-period": {
      title: "Why there are no figures",
      body: [
        "Every figure in EmitIQ is for a reporting period, and this workspace has not set one up.",
        "Nothing is shown rather than a total for a period nobody chose. A period belongs to a company, so the company comes first, and an Admin sets both up.",
      ],
    },
    "add-manual": {
      title: "Adding a figure by hand",
      body: [
        "A figure with no document is recorded on your word and shown as a stated basis, never as a document. It is not verified, and it counts in the totals until a different Admin or Approver accepts it.",
        "Say what it is based on: the meter, the person, the date. It is the only evidence this figure will have, and an assurance provider reads it first.",
        "The grade comes from how you reached the figure. It is set by EmitIQ and cannot be chosen.",
      ],
    },
  },
  scope2: {
    "how-calculated": {
      title: "How Scope 2 is calculated",
      body: [
        "Electricity is priced with the grid emission factor for the site's region (the location-based method): kilowatt-hours multiplied by the factor, in exact kilograms.",
        "The market-based method prices the same electricity using the contractual instruments recorded for it. It is calculated for every period that has instruments, and shown here when one is recorded or when the Advanced view is on.",
        "The two are two inventories of the same electricity. They are never added together, and every receipt names its method.",
      ],
    },
    certificates: {
      title: "Certificates and repricing",
      body: [
        "A certificate is recorded from its document. You confirm what it says and that it meets five checks: it carries the emission-rate attribute, no other instrument claims the same electricity, it was retired by or for the company, it is for the period it is applied to, and it is from the same market as the site. A certificate that fails a check cannot reduce a figure, so it is not recorded.",
        "A certificate covers electricity in its own vintage year only. Recording it reprices that year's market-based Scope 2 figures. A repriced figure keeps its old receipt, marked as replaced, and a filing already issued stays as it was filed.",
        "Repricing now does it at once. Repricing later records the certificate and changes nothing until you press Reprice. A closed period is refused unless an Admin gives a reason, which is kept with the change.",
        "Certificates are drawn down in the order the bills were recorded, not by date, so the same bills recorded in another order can draw one differently. Each receipt says so.",
      ],
    },
    "market-based": {
      title: "Market-based Scope 2",
      body: [
        "Where an instrument covers part of the load, only the uncovered load is priced with the factor. Where none covers it, the figure falls through the hierarchy: certificate, contract, supplier factor, residual mix, and last the location factor.",
        "Load with no instrument is priced at the location factor and each receipt records it. That is a disclosed limitation of the market-based total.",
      ],
    },
  },
  scope3: {
    figures: {
      title: "How Scope 3 is shown",
      body: [
        "Scope 3 is fifteen categories, each a decision. A category nobody has looked at is not a category with nothing in it, so the total says Not assessed when EmitIQ says the scope is not, and never 0.0.",
        "The figures are the ones the engine can price today. They are not placed against a category, because nothing EmitIQ says attributes them to one, and nothing here adds them up beyond EmitIQ's own total.",
      ],
    },
    withheld: {
      title: "Withheld figures",
      body: [
        "A figure whose evidence chain is incomplete is not shown, because its receipt cannot be produced.",
        "It is counted here, with the first link that breaks, so the list does not read as an inventory with fewer lines than it has.",
      ],
    },
    decisions: {
      title: "Category decisions",
      body: [
        "Assessed counts the categories someone has decided about. These counts are decisions, not emissions or estimated coverage shares, and relevant does not mean reported.",
        "An Approver or an Admin records a decision, because it is a statement about the company. Each one stays on record with who decided and when.",
      ],
    },
    categories: {
      title: "The fifteen categories",
      body: [
        "A decision for each category. A category nobody has looked at is not one with nothing in it.",
        "Add data is open for transmission and distribution loss only, the one thing the engine can price. The other categories say Not available yet: they arrive from a purchases file, a supplier's answer or a person's decision, and a category is never guessed.",
      ],
    },
    suppliers: {
      title: "Suppliers' share",
      body: [
        "Suppliers' own answers, once accepted into the inventory, are summed by EmitIQ. A supplier's share is its accepted figures over the period's Scope 3.",
        "This is additional information beside a filing and is never added to a stated total. A supplier that has not answered has no share: nothing accepted and a share of zero are different answers.",
        "A supplier is listed as its workspace, not by name: a supplier is another company's own workspace, and this workspace holds no name for it. Hover a row to see the workspace's full identifier.",
      ],
    },
  },
  upload: {
    // P2 (ADR-0212: Upload, Processing, Documents) owns every entry in this section.
    "how-it-works": {
      title: "How a file is read",
      body: [
        "A file you send is stored first and then checked. A person reads it next, on Processing > In progress, which turns it into a draft record. Nothing becomes a figure until a person accepts it at review.",
        "A bill is read for the usage, the period, the supplier and the meter it states. The site, the scope, the category and the vendor are shown to you as suggestions to confirm, and are never linked on their own.",
      ],
    },
    "file-limits": {
      title: "File limits",
      body: [
        "A file whose contents do not match what it claims to be is refused rather than opened. Encrypted files and PDFs with active content are refused too, and each refusal says what to do next.",
        "The size limit is read from EmitIQ and shown beside the picker. A file over it is turned away in the browser before anything is sent.",
      ],
    },
    "site-hint": {
      title: "Which site is it about",
      body: [
        "Naming a site is optional and is a hint: it pre-fills the match at review, and the reviewer still chooses the site and meter. Nothing a bill says is matched to a site by EmitIQ.",
      ],
    },
    "sample-only": {
      title: "Sample documents only",
      body: [
        "Only synthetic or sample documents may be uploaded in this workspace. Real customer documents are not permitted until EmitIQ records that they are.",
      ],
    },
    "who-can-upload": {
      title: "Who can upload",
      body: [
        "An Admin, an Approver or a Contributor adds a document. Your role reads the library, but a file chosen here would be refused, so there is no picker.",
      ],
    },
    "stages": {
      title: "What the stages mean",
      body: [
        "Received: the file is stored. Checked: its type, size and encryption were accepted, which is not a virus scan. Read: the values were found and a person is waiting to review them.",
        "Matched: you named the site when you sent it. It is your hint, not a match EmitIQ made. Accepted: a person accepted the figure at review.",
      ],
    },
    "other-ways": {
      title: "Other ways in",
      body: [
        "Forwarding bills by email is not available yet, and no address is shown because mail sent to one would go nowhere. Connected feeds and spreadsheet imports are listed with their status.",
      ],
    },
  },
  processing: {
    // P2 (ADR-0212: Upload, Processing, Documents) owns every entry in this section.
    "to-review": {
      title: "Bills to review",
      body: [
        "These bills were read and are waiting for a person. Opening one shows the bill beside what was read from it, and accepting is the only way a bill becomes a figure.",
      ],
    },
    "expected-place": {
      title: "Expected scope and category",
      body: [
        "The scope and category columns are what this kind of document usually is: an electricity bill is Scope 2, a gas bill is Scope 1. They are expected from the type and are decided at review, from the bill and the site.",
      ],
    },
    "in-progress": {
      title: "Files in progress",
      body: [
        "A checked file waits for a person to read it with Read this file. Reading first removes personal details the file only happens to contain, such as a name or phone number on an invoice, then finds the usage, period and supplier. That is why it is a person's step and does not start by itself.",
        "A spreadsheet is imported rather than read. A file stopped in a state nothing moves on has no button: upload it again.",
      ],
    },
    "refused": {
      title: "Refused files",
      body: [
        "A refused file was not opened or kept as evidence. The code says which check refused it and the next step says what to do about it. A file set aside by a person stays listed with its reason.",
      ],
    },
    "missing": {
      title: "Missing bills",
      body: [
        "EmitIQ compares what it expects with what arrived. Missing: it should have arrived and has not. Pending: the period has not closed. Unknown: an on-event source with nothing in it. Confirmed none: someone stated there was no activity, with a reason.",
        "A period that has not closed is never missing, and the six states are never merged into one.",
        "Emissions for a missing bill are absent from every total on every other screen — not zero, absent.",
      ],
    },
    "no-owner": {
      title: "Sources with no owner",
      body: [
        "An expected source with no named operating owner means nobody is accountable for the next period's collection. Missing data is a collection problem and an unnamed owner is a staffing decision, so they are counted apart. Open a source to name its owner.",
      ],
    },
    "source-text": {
      title: "The text of the bill",
      body: [
        "This is the text, not a picture of the page, so it cannot show a logo, a stamp or a signature.",
        "A position is shown only when EmitIQ has re-checked that those characters still say what they said when the figure was read. Where it cannot, it says so rather than pointing somewhere close.",
      ],
    },
    "how-read": {
      title: "How it was read",
      body: [
        "Contact details and names are removed before a document is read, and the prompt and schema versions are recorded so this reading can be reproduced exactly as it ran.",
      ],
    },
    "read-outcome": {
      title: "What reading did",
      body: [
        "Redaction removes incidental personal data from the text before it is read. A count of nothing means it ran and found nothing to remove. No count means this request read nothing, for example a retry of a reading that already ran.",
      ],
    },
    "review-scope": {
      title: "Scope and category",
      body: [
        "This is what accepting would record, worked out from the bill, the site's jurisdiction and the fuel or carrier. It is advice: nothing is recorded until you accept.",
        "A fuel invoice does not always say whether it was burned in a vehicle or in fixed equipment, so that answer is yours. An unsupported fuel is refused with the list of fuels that are supported, and nothing is substituted.",
      ],
    },
    "review-vendor": {
      title: "Vendor",
      body: [
        "The bill states who issued it. If that matches a vendor you already keep, after ignoring case and spacing, it is suggested. It is never linked until you confirm, and a near miss is not suggested.",
        "A vendor is your own note of who bills you. It is not a supplier, which is another company's workspace that answers requests.",
      ],
    },
    "review-site": {
      title: "Site",
      body: [
        "One bill can cover several sites, so there is no default. Choose the site, and the meter where the site has more than one. The site you named at upload is shown as a hint.",
      ],
    },
    "review-accept": {
      title: "Accepting a bill",
      body: [
        "Accepting records the figure with its receipt. An Admin or an Approver accepts; a Contributor prepares the bill and hands it over. Rejecting needs a reason that stays on the document.",
      ],
    },
  },
  documents: {
    // P2 (ADR-0212: Upload, Processing, Documents) owns every entry in this section.
    "library": {
      title: "The library",
      body: [
        "Every document EmitIQ holds, newest first. A search covers the file name, the vendor and the site, and is run by EmitIQ over the whole library, not over the page on screen.",
        "A refused document keeps its code and its remedy. A document with no period is found with the period filter set to none.",
      ],
    },
    "source": {
      title: "Source",
      body: [
        "How the document arrived: Uploaded, Imported or Connector. It is read from the document itself and is never stored as a second copy.",
      ],
    },
    "bulk-edit": {
      title: "What bulk edit checks, and what it cannot",
      body: [
        "Take the inventory out as a workbook, edit it, see exactly what would change, then apply it. Nothing is written until the last step.",
        "The comparison and the apply are two steps and stay two steps: the preview writes nothing, and applying is a narrower set of roles' work, because a corrected reading can move a figure in a report that has already been filed.",
        "A conflict is not an error: somebody corrected the reading after the file was exported, and the person sending it did nothing wrong. Removing a row from a spreadsheet deletes nothing. Applying writes corrections that supersede the reading before them, and the figure each replaces keeps its receipt.",
        "Every changed row is shown before anything is applied, and a high-risk change needs an explicit acknowledgement. A row that writes into a closed period is flagged with the company and period, and only an Admin can apply it: anyone else is told which rows, and asked for no reason. Sending the same file again changes nothing a second time, so a request that times out is safe to repeat.",
      ],
    },
    "bulk-export": {
      title: "The workbook",
      body: [
        "A workbook of every reading in force for this period, with the columns you can edit and a second sheet explaining each one.",
        "The first two columns identify each reading. Leave them alone: they are how a returned file matches what is already here, and how EmitIQ notices if somebody corrects a reading while you are editing.",
      ],
    },
    "import-reject": {
      title: "Rejecting a file",
      body: [
        "A file you will not import should not wait here: it holds up closing the period. Rejecting is final. A corrected file is uploaded as a new document.",
      ],
    },
    "bulk-send": {
      title: "Sending the file back",
      body: [
        "Nothing is applied. This shows what the file would change, so you can look before anything happens.",
      ],
    },
  },
  filings: {
    applicability: {
      title: "How the position is decided",
      body: [
        "One row per company and regulation, with the position EmitIQ decided from the company's recorded turnover, balance sheet total, employees and revenue - not from its emissions. Each row's checks list every rule applied, with its paragraph, its source and any dispute.",
        "Needs figures is not a fifth status but a statement about our data: the rules asked for a fact nobody has stated, and the row names it. An Admin states the financials and runs the determination.",
        "A regulation that is not yet law, or whose enforcement is enjoined, is labelled as such and never counted down to. Assurance is shown as a question with its sources and never an answer, because the sources disagree.",
        "Verification is not human-reviewed. Every regulatory statement here traces to a primary source that an AI model read and quoted; no person at EmitIQ has yet checked those sources by hand. If your own assurance provider needs to rely on them, they will need to check the sources themselves.",
      ],
    },
    readiness: {
      title: "What the percentage means",
      body: [
        "It is the share of this filing's counted checks that have passed, rounded down. Every counted check weighs the same, and a check that has not been run counts as not passed, so an unrun check can never make a filing look closer to done.",
        "It reaches 100 only when every counted check has passed. When nothing is counted it shows a dash, not 100.",
        "It is not the verdict. The verdict word beside it is the only thing that gates approval or issue, and the list below names what is left.",
      ],
    },
    todo: {
      title: "What this list is",
      body: [
        "These are this filing's own checks that have not passed: a bill missing for a site and a month, a figure waiting for a second person, a statement to write, an open question. Each row has the link that fixes it for your role.",
        "Passed checks are behind Show passed. A check that does not apply to this regime is in neither list.",
      ],
    },
    dates: {
      title: "Dates",
      body: [
        "These come from the regulation's own rows, each with a link to the paragraph. A date the regulator is to act by is a date to watch for, not a deadline for you, and nothing here counts down to it.",
        "A length of time, such as a payment window or how long to keep records, has no date of its own because the day it starts is not known yet. No fee amount is shown: none is established.",
      ],
    },
    "first-report": {
      title: "First report",
      body: [
        "For this one filing, the regulation lets a company submit what it already held or was collecting by a set date, or a statement instead of a report. These are the options the rules give, not a recommendation.",
        "Whether your company qualifies depends on facts about its past that EmitIQ cannot see. A statement is drafted only after a person confirms the fact it states, and you put it on your letterhead and send it yourself. Nothing is sent from here.",
      ],
    },
    actions: {
      title: "What these actions do",
      body: [
        "Preview shows the filing as it would read if it were issued now, drawn from today's figures and marked as a rehearsal. It is not the record and has no fingerprint.",
        "Send for approval puts the filing into the approval route and asks the named reviewer to decide, in the app. A reviewer's decision is an approval inside EmitIQ, not a statement that the report is lawfully fit to file; that is a person's judgement.",
        "Under More, Export applies to an issued filing and Email is off until the notification sender is set up. EmitIQ prepares, checks, approves and issues a filing. It does not send one to a regulator, and no screen here means that it has. Filing with the regulator is done outside EmitIQ.",
      ],
    },
    stated: {
      title: "What this filing states",
      body: [
        "These are the figure lines the regulation asks this filing to state, as the report would state them today, each with the calculations behind it. Press a figure to see where it came from.",
        "A line EmitIQ cannot produce says Not assessed, never zero. A line marked as guidance is shown but is not added into the total the regulation asks for.",
        "Scope 2 is stated on the basis named beside the title. The same figures under the other basis are in Disclosures.",
      ],
    },
    "factor-limit": {
      title: "Factors that count carbon dioxide alone",
      body: [
        "Some published factors count carbon dioxide alone. A figure priced from one leaves out methane and nitrous oxide, so it is not a full CO2e figure and is lower than one that includes those gases.",
        "The chip says how many of this line's figures are priced that way and how many tonnes of the line they are. Each of those figures says the same on its receipt, and an issued filing states it in its record.",
      ],
    },
    export: {
      title: "Export",
      body: [
        "Export applies to an issued filing: the record that was frozen, hashed and put on the ledger. The page, the CSV and the workbook come with every issued filing.",
        "A PDF is offered only where this deployment has a renderer set up. Where it has not, the PDF is not offered, and nothing here pretends it exists.",
      ],
    },
    prior: {
      title: "Past filings",
      body: [
        "What the company published before it used EmitIQ, beside what EmitIQ computes. A difference nobody has accounted for holds the filing.",
      ],
    },
    "prior-disclosed": {
      title: "Previously disclosed",
      body: ["Figures held as asserted, not as calculated: what the company published, as it published it."],
    },
    "prior-differences": {
      title: "Differences found",
      body: ["Where this year's figure disagrees with what was published."],
    },
    "boundary-choice": {
      title: "What a filing covers of its group",
      body: [
        "This company's own sites: the figures add up this company's sites only. Every other company in the group is named in the issued record as left out, whether or not it is marked included.",
        "Also its included subsidiaries: the figures also add up every site of each company the ownership record marks included, for the days that company was in the group. A company marked outside the reporting boundary stays out and is named.",
        "The choice is the company's, per filing. EmitIQ records no ownership share, so a filing cannot be set to an equity share.",
        "Changing it after a filing was issued restates that filing: the issued record stays as it was filed and stops matching today's figures, and any open or approved sign-off is withdrawn.",
      ],
    },
  },
  vendors: {
    "vendor-vs-supplier": {
      title: "A vendor is not a supplier",
      body: [
        "A vendor is your own note of a party that bills you: a name, and the accounts and meters it bills. It has no contact and nothing in it is visible to the party named.",
        "A supplier is another company with its own workspace here. It answers requests and shares figures under an agreement, and company names never cross between workspaces.",
        "The figures beside a vendor are those of the bills a person linked to it, and otherwise of the meters billed under its accounts. A document is never matched to a vendor by what it says; a person makes that link.",
      ],
    },
    "retire-vendor": {
      title: "Renaming and retiring a vendor",
      body: [
        "Renaming only corrects how the name is written. A name another vendor already has is refused, and two vendors are never merged.",
        "A vendor is never deleted. Retiring one keeps its accounts, meters and figures exactly as they were, so past reports do not change. A retired vendor cannot receive a new account and is not suggested for a bill. Restoring it lifts that.",
      ],
    },
    "not-recorded": {
      title: "Meters without an account",
      body: [
        "Nobody has said who bills these meters. That is not the same as nobody billing them.",
        "The expected-bills check judges them on their own dates and their site's, exactly as it did before there was a register of accounts.",
      ],
    },
    "accounts": {
      title: "About accounts",
      body: [
        "An account is what a group of meters is billed under: a vendor, a reference and a life. The reference is kept as typed and never matched against a bill.",
        "Ending an account once stops every stream on its meters being expected from that date, and the expected-bills check says the account did it. A start date narrows every meter on the account, so leave it blank unless the account really began then.",
      ],
    },
    "supplier-requests": {
      title: "About supplier requests",
      body: [
        "These are requests this workspace made to its suppliers and the answers that came back. A supplier's own side is their workspace.",
        "A submission carries no document: whether EmitIQ is a controller or a processor of data a supplier sends is a question for counsel, so a supplier states a basis in words.",
        "A supplier needs an account here first. The single-use invitation link is not available yet, so only a supplier this workspace has already dealt with can be asked from this page.",
        "A figure copied from a supplier is graded C and cites their submission and the agreement that permits the copy.",
      ],
    },
  },
  facilities: {
    "sites": {
      title: "About sites",
      body: [
        "A site is where energy is used. Its country decides its emission factors, and its lease treatment decides whether it is inside a filing's boundary.",
        "A site whose lease treatment has not been answered holds every filing it contributes to, with a finding that names it; its figures are still calculated and keep their receipts.",
        "Only an Admin adds or changes sites, because that changes what every figure is about. Register many from a file creates sites, meters and expected sources together, and refuses the whole file if one row is refused.",
      ],
    },
    "site-windows": {
      title: "Periods accounted for",
      body: [
        "Counts the periods (a month or a quarter of one expected bill) of this site's expected sources that have a bill or a person's confirmed none, out of those that are due.",
        "A confirmed none counts as accounted for. A period that has not closed is pending and one that cannot be told is unknown; neither is due, so neither is in the count.",
      ],
    },
    "gap": {
      title: "With a gap",
      body: [
        "A site with a gap has at least one expected bill that is missing in the period, by the expected-bills check. Confirmed none and not yet due are not gaps.",
        "It counts sites, not bills. The Missing tab under Processing lists the bills.",
      ],
    },
    "companies": {
      title: "About companies",
      body: [
        "A company is a legal entity. Each one files for its own sites; a group has one parent, and that is who files for the group.",
        "Ownership is dated: a company bought in August is not the same as one owned for the whole year. The model holds when a company was held, not what share of it, so no share is shown.",
        "A reporting period belongs to one company and one regime, so the same year is filed twice when two regimes apply.",
      ],
    },
    "structure": {
      title: "About the structure",
      body: [
        "The tree shows companies under their parents and each company's sites under it, with what each adds up to by scope in the period. It adds up to the inventory by construction.",
        "A blank cell is a scope with no figure at that node, which is not a zero. A company held by two parents appears under both, so rows are not additive: the total row is the whole organisation.",
      ],
    },
  },
  reports: {
    "library": {
      title: "About the reports",
      body: [
        "These are standard working tables over the figures already in the inventory: pick one, set the dates and any filter, and export it as a CSV or a workbook.",
        "A report is not a filing. It carries no readiness, verdict or regime; the filing pages are under Filings. Every export is labelled as a working report and carries its period, the Scope 2 basis and the time it was run.",
        "A report that cannot be produced is listed with the reason, so its absence is never read as nothing to report.",
      ],
    },
  },
  dashboards: {
    "views": {
      title: "About the dashboards",
      body: [
        "Four fixed views of the same figures: Executive (where we are and what is in the way), Data health (what arrived, what is missing, how sure we are), Operations (what is moving and what is stuck) and Suppliers (who has answered and what it is worth).",
        "A tile is a view and never a source: each one reads an endpoint another screen already uses and opens the receipt or the list that screen opens. There is no tile you design yourself.",
      ],
    },
    "not-here": {
      title: "What these views leave out",
      body: [
        "A year-end illustration. It may be drawn only when the boundary, the sites, the factor edition and the restatement state are the same in both years, and the reads that prove that do not exist yet.",
        "Intensity and the biggest movers. They wait on a decision that has not been made.",
        "An uncertainty band. It needs a published method, and none is named yet.",
        "A tile you design yourself. Composition is where mixed boundaries and mixed periods appear.",
      ],
    },
  },
  audit: {
    engagement: {
      title: "What an Auditor sees here",
      body: [
        "This overview is read-only: it counts what is closed, asked and filed, and it changes nothing. An Auditor's one act, raising an evidence request, is on the Assurance tab.",
        "The Audit ledger and Activity tabs are not offered to an Auditor yet, because an entry has no period or company to narrow it to. Each record's History tab shows the notes, flags, sign-offs and revisions on it, and its receipt shows the ledger entries behind its figures.",
        "An Auditor who is not engaged for a period or particular companies can read the whole chain, and the link to it is on this page.",
      ],
    },
    "engagement-chain": {
      title: "Chain check",
      body: [
        "For an Auditor who is engaged for a period or for particular companies the chain is not shown: a chain with entries removed by a permission could not be verified, so EmitIQ refuses it rather than showing part of it.",
        "An Auditor who is not engaged sees the ledger's own check of the newest entries here, with the count it recomputed.",
      ],
    },
    close: {
      title: "Period close",
      body: [
        "Close a period when its figures are final. A closed period refuses changes; an Admin can reopen it.",
      ],
    },
    "close-how": {
      title: "How closing works",
      body: [
        "The steps are EmitIQ's own blockers, in the order the work runs, and closing is the last of them. There is no step EmitIQ does not check and no percentage: a tick for something nothing verifies would be invented.",
        "Closing is not a flag. A closed period refuses changes that would alter it, so what closing does is shown before the act: how many figures stop accepting changes, what already issued stays as filed, who is told, and how it is undone.",
        "A failed read is not an open period. If the reconciliation or the close cannot be read, this page says so and offers a retry; it never says nothing is outstanding.",
      ],
    },
    ledger: {
      title: "The audit ledger",
      body: [
        "Every recorded act, newest first. Each entry carries the hash of the one before it, so the chain can be checked rather than taken on trust.",
        "Verify re-reads the whole chain and says whether every hash still matches. It cannot say that what was recorded was right, only that it has not been changed since.",
      ],
    },
    "ledger-basis": {
      title: "What is hashed, and why a person is never named",
      body: [
        "An entry's hash is computed over the fields EmitIQ lists in What the chain proves. Everything else on a row - who recorded it, when, and what it was about - is protected by the table being insert-only, not by the chain.",
        "A name beside an entry would be the one thing on the page nobody could re-check against that hash, and the one thing that would read differently after somebody asked to be forgotten.",
        "Fifty entries are shown at a time, so the page stays readable at any size of chain. Every entry is reachable by following the older-entries control under the table.",
      ],
    },
    "ledger-actor": {
      title: "People appear as identifiers",
      body: [
        "A person is recorded as an identifier and never as a name, so this record can be both unalterable and erasable. The name beside it is read when you look; an Auditor sees a role.",
      ],
    },
    activity: {
      title: "Activity",
      body: [
        "Recorded acts in plain words, newest first. Filter by kind of act, by person, or by record.",
        "It is a list, never a check: a list with entries left out by a filter cannot be verified. The audit ledger is where the record is read whole and checked.",
      ],
    },
    assurance: {
      title: "Assurance",
      body: [
        "What the assurance provider asked, what the company answered, and who closed each request. Every answer stays in the thread.",
      ],
    },
    "assurance-engagement": {
      title: "Your engagement and this list",
      body: [
        "A request about a record from a period you are not engaged for is not listed, and an answer that cites such a record shows that it does without naming it. A short list here is not a short queue.",
      ],
    },
    "assurance-raise": {
      title: "Who raises a request",
      body: [
        "An Auditor or an Admin raises a request, about one record at a time. A request somebody inside the company raised against itself is a note, not an assurance request. Everyone else reads the queue, and a Contributor, an Approver or an Admin answers what is put to the organisation.",
      ],
    },
    "assurance-people": {
      title: "Who the identifiers belong to",
      body: [
        "A person is recorded as an identifier and never as a name, so an erasure cannot change an answer in an assurance file after the fact. The name shown is read when you look: the company's own roles see a name, an Auditor sees a role. The export holds identifiers.",
      ],
    },
  },
  targets: {
    "risks-as-now": {
      title: "The register as it stands today",
      body: [
        "This is the register as it stands today. Risks are not versioned, so it cannot be shown as it stood when a report was issued. What was stated is pinned by the filing itself, whose figures and verdict were frozen when it was issued.",
        "Anyone checking a disclosure made last March is reading this year's assessment of the same risks, on whatever scale they are scored against now.",
      ],
    },
    "risks-rescoring": {
      title: "Needs rescoring",
      body: [
        "The scale this risk was scored on has been replaced. The score is left exactly as it was: it was read against the old scale and still means what it meant. Choosing a position on the new one is a judgement EmitIQ does not make for you.",
      ],
    },
    "risks-wording": {
      title: "Needs wording",
      body: [
        "What this risk is complete when, or what would bring its review forward, is a placeholder such as TBC or n/a, or is shorter than twelve characters. It was recorded before that was refused. EmitIQ has not changed it: choose Reword on the row and write what has to be true.",
      ],
    },
    "risks-basis": {
      title: "Why there is no number",
      body: [
        "A score is the position the company chose on its own scale, printed as words. A likelihood multiplied by a consequence, or a five-by-five grid coloured by it, is the product of two ordinals drawn instead of printed, and the scale does not support the difference between a 15 and a 20.",
        "There is no money column either: an exposure in pounds beside a score reads like a financial estimate, and this register holds none.",
        "Recording a risk is an Admin's or an Approver's act, because a register is the company's own account of what it is exposed to. Anyone who does the work may add a response action and complete one.",
      ],
    },
    commitment: {
      title: "The commitment",
      body: [
        "This is the plan as it stands today. Nothing records which version of the target or the assumptions a report was issued against. What was stated is pinned by the filing itself, frozen when it was issued.",
        "EmitIQ does not show progress against this target. Comparing a commitment with measured emissions needs the inventory's own boundary and receipts, and that comparison is not available yet. The inventory is on Scope 1, Scope 2 and Scope 3.",
        "A target states a baseline year and the figure measured in it, a horizon year, and how far below the baseline. The baseline has to be measured, because a target against a projected baseline is a projection of a projection. Modelled values are labelled modelled; none of them is your inventory.",
      ],
    },
    decisions: {
      title: "Why this log cannot be edited",
      body: [
        "A decision that can be changed afterwards is a record of what somebody currently wishes they had decided, which is the opposite of what a decision log is for. Changed your mind? Log the new decision.",
        "An Admin or an Approver logs a decision: the log is the company's own account of what it chose and why, so writing in it is making that statement rather than preparing it.",
      ],
    },
    initiatives: {
      title: "Initiatives",
      body: [
        "Expected and achieved sit side by side and are never added: a projection and a measurement in one column is a plan that reads as a result. An expectation is recorded as one; what an action actually saved is recorded separately, with the document that shows it.",
        "A portfolio total would add up initiatives that overlap as if they were independent reductions, which double-counts. EmitIQ reports overlaps as findings and this page prints them instead of a sum.",
        "A price in money is recorded and shown and is never used: EmitIQ does not guess how a price moves your emissions, so only an input stated as a multiplier can change a modelled figure.",
      ],
    },
    recommendations: {
      title: "How recommendations are worked out",
      body: [
        "Each row is worked out from your own data when you open the tab, and labelled EmitIQ's analysis from your data. No model is asked and nothing is stored.",
        "The kinds are: the largest source of the total, estimates that bills could replace, sites with bills that have not arrived, figures waiting for a second person, and Scope 3 categories nobody has decided. Rows the filing checks count come first, then the largest tonnes.",
        "A row that rests on a figure opens its receipt. A row about something missing shows a count, because a bill that has not arrived has no figure. No saving, cost or deadline is ever shown, and a row is not a statement about what the law asks of you.",
      ],
    },
  },
  admin: {
    "people-engagement": {
      title: "An Auditor's engagement",
      body: [
        "An engagement is a window of data, a last day of access and the filings the Auditor is engaged for. The companies they can read follow from those filings.",
        "No period: no engagement period is recorded, so this Auditor can read every year. An engagement is for a year, not for ever. Set one with Change access period.",
        "Reads every company: no filing is named for this Auditor, so they can read every company in the workspace. A role change cannot add one, so suspend them and invite a new Auditor for the filing, which is what Fix engagement opens.",
      ],
    },
    people: {
      title: "People and roles",
      body: [
        "Who is in this workspace, what each role may do, and who has been invited. A role says what it lets somebody do before it is granted; the Role matrix lists what each role is offered.",
        "Company administration cannot erase or globally anonymise a person's account, which may be shared across workspaces. To stop access here, suspend their workspace access.",
        "Suspending a member removes access to this workspace only. Their other workspaces and account credentials stay available. Restoring access requires a new sign-in and does not extend an expired engagement or restore a globally inactive account.",
        "An Auditor is made by an invitation, never by a role change, because an engagement is a data window, an end date and a filing. Admin and Approver need a second factor before they can act. Changing a role is recorded in the authentication audit log, which holds a digest of an address rather than the address.",
      ],
    },
  },
  account: {
    profile: {
      title: "You",
      body: [
        "Your display name is shown to colleagues in every workspace you belong to. Your email and your role in this workspace are shown here and are not changed here.",
        "Admin and Approver accounts need a second factor before they can be used, because those roles can approve a filing. That is a deliberate rule, not a setting. This page shows whether one is set up; setting one up or resetting it is not done from here.",
        "A photo, a job title and a phone number are not kept: the product has nowhere to store them yet.",
      ],
    },
    preferences: {
      title: "Time zone and language",
      body: [
        "The language sets the order of every date (Oct 31, 2026 or 31 Oct 2026, the month always a name) and the clock (12-hour or 24-hour). Numbers read the same in both.",
        "Every time is shown in your time zone, with the zone named, and an event is dated by the day it fell on there. A calendar date, such as a reporting period, does not move.",
        "These are kept for you in this workspace; another workspace keeps its own.",
      ],
    },
    password: {
      title: "Password",
      body: [
        "Changing your password signs every other session of yours out and keeps the one you are using, so a session you did not start does not survive the change.",
        "The rules for a password are EmitIQ's and are shown when one is refused. The current password is asked for every time.",
      ],
    },
    "two-step": {
      title: "Two-step sign-in",
      body: [
        "A passkey lets you sign in with your device's fingerprint, face or screen lock instead of typing a password. The fingerprint or face never leaves your device.",
        "A passkey is an addition: your password and authenticator codes keep working, so you can always sign in without it. Passkeys are new here and have not been tried on every device; they have met only a software authenticator.",
        "Recovery codes are shown once when two-step sign-in is set up. Making new ones is not available yet.",
        "Adding a passkey asks for your password again, and the last way to sign in cannot be removed.",
      ],
    },
    sessions: {
      title: "Active sessions",
      body: [
        "Your live sessions in this workspace, newest first, with the one you are using marked. If you do not recognise one, end it here. The session you are using is not offered: use Sign out for that.",
        "A history of past sign-ins is not available yet. The sign-in events of the whole workspace are in the Admin log, for an Admin.",
      ],
    },
    notifications: {
      title: "Notifications",
      body: [
        "In-app notifications are always on and are listed under Action items. Email notifications are not being sent yet, so the two choices here record what you want for when they are, and the Email column shows what you chose. They are yours in this workspace, not a company-wide switch.",
        "A summary says how many items are waiting and links to your list, once each time notification emails go out, instead of one email per item. It only matters while notification emails are on, and it is kept if you switch them off.",
        "EmitIQ keeps one email choice and one summary choice per person, not one per kind of notification. Password-reset and invitation emails are unchanged.",
      ],
    },
    display: {
      title: "Display",
      body: [
        "The colour theme applies at once and is kept in this browser. Another device keeps its own.",
        "Density, number format, Units, and a default site and period are not available yet: nothing reads them, and a setting that is saved and ignored is worse than none. Units in particular wait on a decision about how a receipt re-performs in a unit its document did not state.",
        "A personal API token is not offered: the product does not issue one.",
      ],
    },
    scope2: {
      title: "Scope 2 view",
      body: [
        "By default one Scope 2 number is shown everywhere, location-based. The market-based column and its basis appear on their own when an instrument is recorded in the period; this switch shows them for you without one.",
        "It is a display preference, kept in this browser, and not a statement about what any law requires. A receipt always names the method it used, and a filing states its basis when it is issued.",
      ],
    },
  },
  support: {
    how: {
      title: "How to raise a ticket",
      body: [
        "This is an email contact, not a ticket queue. Note the screen you were on, and the reference shown with an error if there was one. The button opens your email with those fields ready; nothing is sent from here.",
        "Leave out personal data and the contents of documents. Support cannot open your workspace, so describe the screen instead.",
      ],
    },
    expect: {
      title: "What to expect",
      body: [
        "The reply time is the one on the Reply time tile, when there is one. No support hours or ticket number is promised, and no uptime is claimed: a number nobody measured is not printed. Status is what was measured when this page opened.",
        "A request about your data is recorded on Your data requests, which is a receipt and not a handled request.",
        "A guide is released to a workspace once it has been reviewed. Ask by email for the one you need. How your data is processed, and how to read the screens, are in Help.",
      ],
    },
  },
  record: {
    calculation: {
      title: "How a figure is calculated",
      body: [
        "The usage multiplied by the locked emission factor gives exact kilograms. Dividing by 1,000 and rounding for display gives tonnes, and nothing is rounded before that.",
        "The receipt is the full account: the factor's publisher and edition, the GWP set, the licence attribution and the ledger entry. Open it from the button on the card.",
      ],
    },
    status: {
      title: "About this status and correcting a record",
      body: [
        "The path shows where this figure stands: recorded, priced, verified and in the ledger. Approved is drawn only where a review route is published for this kind of record (an Admin publishes one in Admin, under Approval routes), or where an approval already stands.",
        "A recorded figure is never edited. A correction is a new revision that supersedes this one, so both stay readable and a report can say which was in force when it was filed.",
        "Before a statement becomes a record, a reviewer corrects a field on the extraction, with a reason, on the document itself. After it is a record, a replacement bill is verified and promoted as superseding this one, and any approval pending on this version is ended at that moment. Roles that may upload are offered Upload a replacement bill beside the document.",
        "Correcting a record in place from this page is not available yet. It is an open requirement, not something a disabled button implies.",
      ],
    },
    "document-review": {
      title: "How a document becomes a figure",
      body: [
        "A document is read, then a person reviews it, and only a reviewed document can produce figures. That decision is taken on the review screen, under Processing, where the pages are; there is no second accept control on this page.",
        "The figures listed here are the records that cite this document. A record that was later corrected is listed as superseded and is in no current total.",
      ],
    },
    "no-verifier": {
      title: "No verifier recorded",
      body: [
        "No verifier is recorded against this record, so its figures report the evidence chain as breaking at the verification step. The absence is never treated as approval.",
        "A figure entered by hand arrives like this and is accepted by a different Admin or Approver.",
      ],
    },
    superseded: {
      title: "Not the version in force",
      body: [
        "A later revision supersedes this one, so its figures are in no current total. It still resolves and stays readable, because it may already have been quoted.",
        "The revision history says what replaced it.",
      ],
    },
    "no-document": {
      title: "A figure with no bill",
      body: [
        "A figure typed in by hand has no bill. Its receipt reports the evidence as resting on a stated basis rather than on a document, which is legitimate and is why the first link of its trail reads Basis stated.",
      ],
    },
    document: {
      title: "About the document view",
      body: [
        "The link to the stored file is short-lived and is issued each time this tab opens. The text below is what EmitIQ read from the document, with the characters each figure was read from marked where they can still be confirmed.",
        "The page itself is not drawn here. A file somebody else chose is kept out of this application's own origin until there is a separate document origin and a content-security policy to hold it there.",
      ],
    },
    revisions: {
      title: "Revision history",
      body: [
        "Which revision is in force is derived from the chain, as the one nothing supersedes, and is not stored on a row. Only that revision contributes to a total.",
        "A correction appears here as a new revision, never as a change to an old one.",
      ],
    },
    // ---- record page trust upgrades (P15): one contiguous block, keep additions together ----
    "evidence-meter": {
      title: "The evidence meter",
      body: [
        "Six links, in order: referenced, stored, retrievable, verified, priced and in the ledger. Filled means the link was established. Hollow means it is the first link that breaks, and its reason is on the segment. Dashed means it was not checked. Grey means it does not apply to this figure, and says why.",
        "EmitIQ stops at the first break, so links after it are shown as not checked rather than as met or broken. There is no percentage: a count of links would claim to have looked at ones nobody did. A segment opens the part of the receipt that explains it.",
      ],
    },
    "sign-off": {
      title: "About the sign-off",
      body: [
        "Each line is one decision on one stage: who made it, in what role, when, and the ledger entry that holds it. The time is shown in UTC with your own time beside it.",
        "A sign-off is of one revision and the figure priced from it. It records a person's judgement of that figure and is not a statement that a report is fit to file. The reason a reviewer gave is kept with the decision and is not shown here.",
      ],
    },
    "stale-approval": {
      title: "Why an approval goes stale",
      body: [
        "An approval describes the revision and the figures that were in force when it was given. When a correction replaces the revision, or a recalculation changes a figure, the approval is shown as stale and the list under it says exactly what changed.",
        "Nobody rejected anything: the decision stands in the ledger, and what was judged has been replaced. The record needs a fresh review before it counts as approved again.",
      ],
    },
    "history-scope": {
      title: "What the timeline covers",
      body: [
        "The timeline merges three kinds of event for this record: system events such as pricing and anchoring, people's notes, flags and tasks, and approval decisions. Each event that has a ledger entry links to it.",
        "It is a filtered list of recorded acts and not a check of the record. The Audit ledger page reads the chain whole and checks it. A note's text, a flag's reason and a reviewer's reason are not copied here.",
        "A role that does not read the ledger, or an Auditor whose engagement narrows it, sees the notes, flags, tasks, sign-offs and revisions, read-only. The ledger entries behind a figure are on its receipt.",
      ],
    },
  },
  // ---- G-B (2026-11-02): text moved off the screens still on their old layout. One contiguous block;
  // keep additions together. A screen's entry is deleted from here only with the control that shows it.
  legacy: {
    "personal-requests": {
      title: "What recording a request does",
      body: [
        "This records the kind of request you select, for your account only. It has not been assessed or routed to an authorised handler, and no response deadline or service commitment is set.",
        "Signing in is not a completed requester-identity check. This form does not accept requests from non-users or representatives, and it does not erase data or carry out the requested action.",
      ],
    },
    "bulk-role": {
      title: "Who can change readings",
      body: [
        "Comparing an edited file against the inventory is work an Admin, an Approver or a Contributor does. A Viewer or an Auditor can read the inventory and download it - the editable export above is theirs - but cannot compare an edited file.",
        "Send the file to one of those roles, or ask an Admin to change your role on People and roles.",
      ],
    },
    "assistant-conditions": {
      title: "When the AI assistant is on",
      body: [
        "The assistant is off until three things are true: the add-on is in your agreement, a model is connected, and an Admin has switched it on. The conditions beside the heading show which of the three is missing.",
        "Auditors never use it: an assurance provider's reads are not routed to a model you chose. Spend is an estimate from the rates entered here, and the provider's own invoice is the truth.",
      ],
    },
    "emissions-all": {
      title: "All emissions",
      body: ["Every figure, by scope and period. A figure opens its receipt."],
    },
    "financials-page": {
      title: "Financials",
      body: [
        "The facts that decide what is owed: a company's size, its revenue and five yes or no questions. They are stated once a year by an Admin, and what they decided is read on Filings.",
      ],
    },
    "financials-state": {
      title: "Stating a company's financials",
      body: [
        "State a company's financials for one year. It determines the company's position in the same step, and the result is read on Filings (Applicability), with the reasoning behind each position.",
      ],
    },
    "financials-determine": {
      title: "Running a determination",
      body: [
        "Works out every company's position under every current regime for one year. With no figures stated it still answers, with one position per company and regime that says what it needs. The results also appear on Filings, with the reasoning behind each.",
      ],
    },
  },
} as const satisfies Record<string, Record<string, InfoEntry>>;

export type InfoSection = keyof typeof INFO_TEXT;

/** `section.topic`, typed from the object above, so a key a component names that does not exist is a
 * compile error as well as a failing test. */
export type InfoKey = {
  [S in InfoSection]: `${S}.${keyof (typeof INFO_TEXT)[S] & string}`;
}[InfoSection];

/** The entry for a key. Throws for a key that is not defined: the type already refuses it, so
 * reaching the throw means a cast, and an unexplained control is worse than a loud failure. */
export function infoEntry(key: InfoKey): InfoEntry {
  const [section, topic] = key.split(".") as [InfoSection, string];
  const entries: Readonly<Record<string, InfoEntry>> = INFO_TEXT[section];
  const entry = entries[topic];
  if (!entry) throw new Error(`infoText: no entry "${key}"`);
  return entry;
}

/** Every defined key, for Help and for the guard test. */
export function allInfoKeys(): InfoKey[] {
  const out: string[] = [];
  for (const [section, entries] of Object.entries(INFO_TEXT)) {
    for (const topic of Object.keys(entries)) out.push(`${section}.${topic}`);
  }
  return out as InfoKey[];
}
